import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  Autocomplete,
} from "@mui/material";
import CustomTypography from "../../globalStyles/fonts";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { shippingServiceMapService } from "../../utils/services/shippingServiceMapService";
import { providerService } from "../../utils/services/providerService";
import ClientAutocomplete from "../clientAutocomplete";

const ShippingServiceMapModal = ({
  open,
  handleClose,
  onAddData,
  selectedService,
}) => {
  const [shippingServiceMapDetails, setShippingServiceMapDetails] = useState({
    service: "",
    shipping_service: null,
    provider: null,
    client_id: "",
  });
  const [selectedClient, setSelectedClient] = useState(null);
  const [shippingServiceData, setShippingServiceData] = useState([]);
  const [providers, setProviders] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const clientId = localStorage.getItem("client_id");
  useEffect(() => {
    if (selectedService) {
      setSelectedClient(selectedService.client_id);
      setShippingServiceMapDetails({
        service: selectedService.service || "",
        shipping_service: selectedService.shipping_service || null,
        provider: { name: selectedService.provider } || { name: "" },
        client_id: selectedService.client_id || "",
      });
    } else {
      resetForm();
    }
  }, [selectedService]);

  useEffect(() => {
    fetchShippingService();
    fetchProviders();
  }, []);

  const fetchShippingService = async () => {
    try {
      const response = await shippingServiceMapService.fetchShippingService();
      setShippingServiceData(response.data || []);
    } catch (error) {
      console.error("Error fetching shipping services:", error);
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await providerService.fetch();
      setProviders(response.data.filter((x) => x.type == "OMS") || []);
    } catch (error) {
      console.error("Error fetching providers:", error);
    }
  };

  const resetForm = () => {
    setShippingServiceMapDetails({
      service: "",
      shipping_service: null,
      provider: null,
      client_id: "",
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!shippingServiceMapDetails.service)
      errors.service = "Service is required.";
    if (!shippingServiceMapDetails.shipping_service)
      errors.shipping_service = "Shipping service is required.";
    if (!shippingServiceMapDetails.provider)
      errors.provider = "Provider is required.";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setShippingServiceMapDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveOrEdit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      if (selectedService) {
        await shippingServiceMapService.edit(
          {
            service: shippingServiceMapDetails.service,
            shipping_service: shippingServiceMapDetails.shipping_service?._id,
            provider: shippingServiceMapDetails?.provider?.name,
            client_id: clientId ? clientId : selectedClient?._id,
          },
          selectedService._id
        );
        toast.success("Service edited successfully!");
      } else {
        await shippingServiceMapService.create({
          service: shippingServiceMapDetails.service,
          shipping_service: shippingServiceMapDetails.shipping_service?._id,
          provider: shippingServiceMapDetails?.provider?.name,
          client_id: clientId ? clientId : selectedClient?._id,
        });
        toast.success("Service created successfully!");
      }
      onAddData && onAddData();
      handleClose();
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Failed to save Shipping Service Map.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      aria-labelledby="form-dialog-title"
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <DialogTitle id="form-dialog-title">
          {selectedService
            ? "Edit Shipping Service Map"
            : "Create Shipping Service Map"}
        </DialogTitle>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <Divider />
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={clientId ? 12 : 6}>
            <CustomTypography>Service</CustomTypography>
            <TextField
              sx={{ marginTop: 1 }}
              size="small"
              name="service"
              value={shippingServiceMapDetails.service}
              onChange={handleInputChange}
              fullWidth
              error={!!formErrors.service}
              helperText={formErrors.service}
            />
          </Grid>
          <Grid item xs={12} sm={clientId ? 12 : 6}>
            <CustomTypography>Shipping Service</CustomTypography>
            <Autocomplete
              options={shippingServiceData}
              getOptionLabel={(option) => option.service || ""}
              value={shippingServiceMapDetails.shipping_service}
              onChange={(_, value) =>
                setShippingServiceMapDetails((prev) => ({
                  ...prev,
                  shipping_service: value,
                }))
              }
              renderInput={(params) => (
                <TextField
                  sx={{ marginTop: 1 }}
                  {...params}
                  fullWidth
                  error={!!formErrors.shipping_service}
                  helperText={formErrors.shipping_service}
                  size="small"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={clientId ? 12 : 6}>
            <CustomTypography>Provider</CustomTypography>
            <Autocomplete
              options={providers}
              getOptionLabel={(option) => option.name || ""}
              value={shippingServiceMapDetails.provider}
              onChange={(_, value) =>
                setShippingServiceMapDetails((prev) => ({
                  ...prev,
                  provider: value,
                }))
              }
              renderInput={(params) => (
                <TextField
                  sx={{ marginTop: 1 }}
                  {...params}
                  fullWidth
                  error={!!formErrors.provider}
                  helperText={formErrors.provider}
                  size="small"
                />
              )}
            />
          </Grid>

          {!localStorage.getItem("client_id") && (
            <Grid item xs={12} sm={6}>
              <CustomTypography>Choose Client</CustomTypography>
              <ClientAutocomplete
                hideLabel
                selectedClient={selectedClient}
                onClientChange={(client) => setSelectedClient(client)}
                errorMessage={formErrors.client_id}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSaveOrEdit} variant="contained">
          {selectedService ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShippingServiceMapModal;
